import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, SpecraActionList, SpecraEyebrow, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowRight, BarChart3, Calendar, CalendarCheck, ClipboardCheck, DollarSign, FileQuestion, SendHorizontal, Stamp } from 'lucide-react';
import { useState } from 'react';
import { AssignedActionList } from '../../../../components/delivery/widgets/AssignedActionList';
import { useProgramFinancialSummary } from '../../../../components/delivery/widgets/useProgramFinancialSummary';
import { financialSectionVisible, programRef } from '../../../../lib/access/types';
import { useView } from '../../../../lib/access/useView';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { useCmAccess } from '../../Deliverables/access';
import { LogEventDrawer } from '../../Deliverables/components/LogEventDrawer';
import { OccurrenceWindow, useDuties, useOccurrenceDecisions, useOccurrences } from '../../Deliverables/hooks';
import { addDays, todayISO } from '../../Deliverables/lib/format';
import { waitingEvents } from '../../Deliverables/lib/rule';
import { useProgram } from '../hooks';
import { useProgramModificationChangeSets } from '../v2/modificationHooks';
const MINE_ROW_CAP = 5;
/** The last calendar day of `iso`'s month ("2026-09-30" for any September day). */ function monthEnd(iso) {
    const [y, m] = iso.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
    return addDays(next, -1);
}
function sumStates(counts, states) {
    return states.reduce((n, s)=>n + (counts?.[s] ?? 0), 0);
}
/**
 * The deliverable signals, from the contract model (D112 #6, #8): late over
 * the program's KPI window (the contract period containing today, plus open
 * dates carried over — D104), still owed and due between today and month end,
 * and proposals awaiting approval. Every count is pangaea's `state_counts`;
 * page_size 1 asks for the counts without the rows.
 */ function useDeliverableSignals(programId) {
    const today = todayISO();
    const period = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        pageSize: 1
    });
    const month = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        dueFrom: today,
        dueTo: monthEnd(today),
        pageSize: 1
    });
    const decisions = useOccurrenceDecisions(programId, {
        openOnly: true
    });
    return {
        isLoading: period.isLoading || month.isLoading || decisions.isLoading,
        late: period.isError ? null : period.data?.stateCounts.late ?? 0,
        dueThisMonth: month.isError ? null : sumStates(month.data?.stateCounts, [
            'due_soon',
            'upcoming'
        ]),
        awaiting: decisions.isError ? null : decisions.data?.decisions ?? []
    };
}
const MINE_TESTIDS = {
    container: Testids.programHome.mineContainer,
    row: Testids.programHome.mineRow,
    empty: Testids.programHome.mineEmpty,
    error: Testids.programHome.mineError,
    more: Testids.programHome.mineMore,
    resolve: Testids.programHome.mineResolve
};
// A record can feed both an aggregate Do Next signal (e.g. an overdue
// deliverable assigned to you contributes to the "N overdue" count) and a
// Mine row — that's deliberate, not a duplicate. The signal is a program-
// level aggregate; the Mine row is the underlying personal record. Dedupe
// inside AssignedActionList only guards against the same (kind, id)
// appearing twice WITHIN Mine.
function MineGroup({ programId }) {
    // SAD-143: a Correction can be assigned to someone with no self-assessment
    // access (e.g. a Contracts assignee). Their program grant opens the shell — so
    // this Mine group renders for them — but the Audits page refuses them, which
    // turned a Mine-group Correction row (a link into /audits) into a dead end
    // with no way to resolve. When the viewer can't open the audit, offer the same
    // inline Resolve the global Dashboard's card does instead of that link; viewers
    // who CAN open it keep their existing deep link.
    const canViewSelfAssessment = useProgram(programId).data?.canViewSelfAssessment ?? false;
    return /*#__PURE__*/ _jsx(AssignedActionList, {
        programId: programId,
        heading: "Assigned to you",
        cap: MINE_ROW_CAP,
        testids: MINE_TESTIDS,
        enableResolve: !canViewSelfAssessment
    });
}
/**
 * The program-home Do Next queue (LAYOUT-SPEC §2A): 3–6 ranked actions, each
 * with a verb and a destination. Derived from the same real signals the KPIs
 * read (overdue/ due deliverables, audit state, missing scores/financials) —
 * no queue table, no new RPC. Empty when the program is genuinely on track.
 */ export function DoNextQueue({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const signals = useDeliverableSignals(programId);
    const summary = useProgramFinancialSummary(programId);
    const revealFinancials = financialSectionVisible(useView(programRef(programId)).fieldMasked);
    // Review items (D93) the uploads couldn't settle; the program's change sets
    // hold nothing applied until each is decided.
    const changeSets = useProgramModificationChangeSets(programId, useTenantId());
    const openReview = (changeSets.data ?? []).reduce((n, cs)=>n + cs.openReviewCount, 0);
    // S6: an event deliverables wait on (a kickoff, a PI Planning session) is
    // logged from here; until it is, their dates are projected, never late.
    const access = useCmAccess(programId);
    const duties = useDuties(programId, {
        includeRules: true
    });
    const waiting = waitingEvents(duties.data?.rules ?? [], duties.data?.duties ?? [], duties.data?.checkCodes ?? {});
    const [logKind, setLogKind] = useState(null);
    // Mine is a separate query from the Do Next signals below — its own
    // loading/error/empty states must never block or delay theirs.
    const mine = /*#__PURE__*/ _jsx(MineGroup, {
        programId: programId
    });
    if (signals.isLoading || summary.isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                mine,
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-24 w-full"
                })
            ]
        });
    }
    const kpi = summary.data?.kpiSnapshot;
    const items = [];
    // First: until these are decided, the contract changes behind them stay
    // unapplied. A roster that failed to load is never read as zero: the row
    // says the count is unknown and still points at the review.
    if (changeSets.isError && !changeSets.data) {
        items.push({
            id: 'review-items-unknown',
            label: 'Check the contract changes for facts that need your call',
            meta: "Couldn't count",
            href: `${base}/docs?tab=modifications`,
            icon: /*#__PURE__*/ _jsx(FileQuestion, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (openReview > 0) {
        items.push({
            id: 'review-items',
            label: "Decide the contract facts the documents didn't settle",
            meta: `${openReview} need${openReview === 1 ? 's' : ''} your call`,
            href: `${base}/docs?tab=modifications`,
            icon: /*#__PURE__*/ _jsx(FileQuestion, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    }
    // A count that failed to load is never read as zero: the row says so and
    // still points at the list.
    if (signals.late === null) {
        items.push({
            id: 'overdue-unknown',
            label: 'Check late deliverables',
            meta: "Couldn't count",
            href: `${base}/deliverables?state=late`,
            icon: /*#__PURE__*/ _jsx(SendHorizontal, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (signals.late > 0) {
        items.push({
            id: 'overdue',
            label: 'Submit late deliverables — attach the files and delivery emails',
            meta: `${signals.late} late`,
            href: `${base}/deliverables?state=late`,
            icon: /*#__PURE__*/ _jsx(SendHorizontal, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'danger'
        });
    }
    for (const e of waiting){
        const n = e.dutyIds.length;
        items.push({
            id: `log-event:${e.kind}`,
            label: `Log ${e.label}`,
            meta: `${n} deliverable${n === 1 ? '' : 's'} projected until it is`,
            // Without write access the row opens the deliverable that waits on it.
            ...access.canWrite ? {
                onClick: ()=>setLogKind(e.kind)
            } : {
                href: `${base}/deliverables/${e.dutyIds[0]}`
            },
            icon: /*#__PURE__*/ _jsx(CalendarCheck, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    // D112 #8: proposals wait on a second person; the row opens the
    // awaiting-approval list, where they can be approved together.
    const awaiting = signals.awaiting;
    if (awaiting === null) {
        items.push({
            id: 'awaiting-approval-unknown',
            label: 'Check proposals awaiting approval',
            meta: "Couldn't count",
            href: `${base}/deliverables?awaiting=1`,
            icon: /*#__PURE__*/ _jsx(Stamp, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (awaiting.length > 0) {
        items.push({
            id: 'awaiting-approval',
            label: `Awaiting approval (${awaiting.length})`,
            meta: 'Proposals a second person must approve',
            href: `${base}/deliverables?awaiting=1`,
            icon: /*#__PURE__*/ _jsx(Stamp, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    const hasAudit = !!kpi?.hasAudit && (kpi?.latestAuditTotal ?? 0) > 0;
    if (!hasAudit) {
        items.push({
            id: 'audit',
            label: 'Run the SDS audit to establish a compliance score',
            href: `${base}/audits`,
            icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if ((kpi?.latestAuditPass ?? 0) < (kpi?.latestAuditTotal ?? 0)) {
        const gap = (kpi?.latestAuditTotal ?? 0) - (kpi?.latestAuditPass ?? 0);
        items.push({
            id: 'audit-gap',
            label: 'Resolve failing audit checks — each becomes a correction',
            meta: `${gap} failing`,
            href: `${base}/audits`,
            icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    }
    if (kpi && !kpi.hasCpars && !kpi.hasQasp) {
        items.push({
            id: 'scores',
            label: 'Enter customer scores — CPARS and QASP',
            href: `${base}/quality`,
            icon: /*#__PURE__*/ _jsx(BarChart3, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    const noCurrentMonth = !summary.data?.financialHealth?.hasCurrent;
    if (revealFinancials && noCurrentMonth) {
        items.push({
            id: 'financials',
            label: "Record this month's financials",
            href: `${base}/financial`,
            icon: /*#__PURE__*/ _jsx(DollarSign, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    if (signals.dueThisMonth !== null && signals.dueThisMonth > 0) {
        items.push({
            id: 'due',
            label: 'Deliverables due this month — plan submissions',
            meta: `${signals.dueThisMonth} due`,
            href: `${base}/deliverables`,
            icon: /*#__PURE__*/ _jsx(Calendar, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'neutral'
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            mine,
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.programHome.doNextContainer,
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        className: "block",
                        children: "Do Next"
                    }),
                    items.length === 0 && changeSets.isLoading ? // Not "nothing needs attention" until the review count is in.
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-3",
                        "data-testid": Testids.programHome.doNextPending,
                        children: /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-10 w-full"
                        })
                    }) : items.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        tone: "muted",
                        className: "mt-3",
                        children: "Nothing needs attention — deliverables are on track, the audit is passing, and scores are recorded."
                    }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                        className: "mt-1",
                        items: items.slice(0, 6),
                        renderLink: renderRouterLink,
                        getItemTestId: ()=>Testids.programHome.doNextRow,
                        trailing: /*#__PURE__*/ _jsx(ArrowRight, {
                            className: "h-3 w-3"
                        })
                    })
                ]
            }),
            logKind !== null ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: logKind,
                onClose: ()=>setLogKind(null)
            }) : null
        ]
    });
}
